import React, { useState, useEffect } from "react";
import CommonGrid from "./CommonGrid";
import CommonHeader from "./CommonHeader";
import usersApi, { ReportInterval } from "../services/users/UserApi"; // Import the UserApi instance and ReportInterval enum
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer } from "recharts";
import type { TooltipProps } from "recharts";

// Helper: format date from seconds to readable string
function formatDate(ts: number) {
  // Convert seconds to ms, then to date string (e.g. '2025-11-07')
  const d = new Date(ts * 1000);
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

// Helper: map API array to recharts data, supporting per-user series for "All"
function getWeeklyChartData(data: any[], selectedUser: string) {
  if (selectedUser && selectedUser !== "All") {
    // Only selected user: one line/bar per metric
    return data.map((item: any) => {
      const userStats = item.msgReport?.msgCount?.find(
        (userItem: any) => userItem.user.username === selectedUser
      );
      return {
        date: formatDate(item.date),
        Sent: userStats ? userStats.sent : 0,
        Delivered: userStats ? userStats.delivered : 0,
        Failed: userStats ? userStats.failed : 0,
        Received: userStats ? userStats.received : 0,
        Read: userStats ? userStats.read : 0,
        username: selectedUser,
      };
    });
  }

  // "All": one row per date, each user as a property (Sent only), plus all metrics for tooltip
  const allUsers = Array.from(
    new Set(
      data
        .flatMap((item: any) =>
          item.msgReport?.msgCount?.map((userItem: any) => userItem.user.username) || []
        )
    )
  );

  return data.map((item: any) => {
    const row: any = { date: formatDate(item.date) };
    allUsers.forEach((username) => {
      const userStats = item.msgReport?.msgCount?.find(
        (userItem: any) => userItem.user.username === username
      );
      row[username] = userStats ? userStats.sent : 0;
      // For tooltip: store all metrics for this user for this date
      row[`__${username}_all`] = userStats
        ? {
            sent: userStats.sent,
            delivered: userStats.delivered,
            failed: userStats.failed,
            received: userStats.received,
            read: userStats.read,
            user: { username },
          }
        : {
            sent: 0,
            delivered: 0,
            failed: 0,
            received: 0,
            read: 0,
            user: { username },
          };
    });
    return row;
  });
}

const Dashboard = () => {
  const [dashboardData, setDashboardData] = useState<any>(null);
  const [activeTab, setActiveTab] = useState<string>("users");
  // analyticsData is now an array of daily stats
  const [analyticsData, setAnalyticsData] = useState<any[]>([]);
  const [selectedUser, setSelectedUser] = useState<string>("");
  const [chartType, setChartType] = useState<"bar" | "line" | "pie">("bar");
  // New state for analytics query params
  const [reportType, setReportType] = useState<ReportInterval>(ReportInterval.DAILY);
  const [startTime, setStartTime] = useState<string>("");
  const [endTime, setEndTime] = useState<string>("");
  const [filterParams, setFilterParams] = useState<any>(null);

  useEffect(() => {
    // Fetch dashboard data from the API
    const fetchData = async () => {
      try {
        const response = await usersApi.getDashboardData();
        if (response.success) {
          setDashboardData(response.value.data); // Adjusted to match the response structure
        } else {
          console.error("Failed to fetch dashboard data:", response);
        }
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
      }
    };
    fetchData();
    // Trigger default analytics load (DAILY) on mount
    setFilterParams({ type: ReportInterval.DAILY });
  }, []);


  // Fetch analytics data when filterParams changes
  useEffect(() => {
      setAnalyticsData([]);
    if (!filterParams) return;
    const fetchAnalyticsData = async () => {
      try {
        const response = await usersApi.getAnalyticsData(filterParams);
        if (response.success) {
          setAnalyticsData(Array.isArray(response.value.data) ? response.value.data : []);
        } else {
          console.error("Failed to fetch analytics data:", response);
          setAnalyticsData([]);
        }
      } catch (error) {
        console.error("Error fetching analytics data:", error);
        setAnalyticsData([]);
      }
    };
    fetchAnalyticsData();
  }, [filterParams]);

  // Handler for filter button
  const handleAnalyticsFilter = (e: React.FormEvent) => {
    e.preventDefault();
    let params: any = {};
    if (reportType === ReportInterval.CUSTOM) {
      if (startTime) params.startTime = new Date(startTime).getTime();
      if (endTime) params.endTime = new Date(endTime).getTime();
      // Do NOT include type: 'CUSTOM' in params
    } else {
      params.type = reportType;
    }  
    setFilterParams(params);
  };

  const handleTabClick = (tab: string) => {
    setActiveTab(tab);
  };

  if (!dashboardData) {
    return (
      <div className="dark:text-gray-50 text-center mt-[200px]">Loading...</div>
    );
  }

  return (
    <div className="flex h-[calc(100vh-67px)]">
      {/* Main Content */}
      <div className="flex-1">
        {/* Header */}
        <CommonHeader title="Dashboard" />

        {/* Dashboard Content */}
        <main className="flex-1 p-4 sm:p-8 lg:p-10">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 sm:gap-8 lg:gap-4">
            {/* Existing Tabs */}
            <div
              onClick={() => handleTabClick("users")}
              className={`cursor-pointer rounded-xl  ${
                activeTab === "users"
                  ? "bg-gray-600 dark:bg-gray-500"
                  : "bg-gray-900"
              }`}
            >
              <CommonGrid
                title="Total Users"
                value={dashboardData.totalUsers}
              />
            </div>
            <div
              onClick={() => handleTabClick("groups")}
              className={`cursor-pointer rounded-xl  ${
                activeTab === "groups"
                  ? "bg-gray-600 dark:bg-gray-500"
                  : "bg-gray-900"
              }`}
            >
              <CommonGrid
                title="Total Groups"
                value={dashboardData.totalGroups}
              />
            </div>
            <div
              onClick={() => handleTabClick("contacts")}
              className={`cursor-pointer rounded-xl  ${
                activeTab === "contacts"
                  ? "bg-gray-600 dark:bg-gray-500"
                  : "bg-gray-900"
              }`}
            >
              <CommonGrid
                title="Total Contacts"
                value={dashboardData.totalContacts}
              />
            </div>
            <div
              onClick={() => handleTabClick("messages")}
              className={`cursor-pointer rounded-xl  ${
                activeTab === "messages"
                  ? "bg-gray-600 dark:bg-gray-500"
                  : "bg-gray-900"
              }`}
            >
              <CommonGrid
                title="Messages Sent"
                value={dashboardData.msgReport.sent}
              />
            </div>
            {/* New Analytics Tab */}
            {/* <div
              onClick={() => handleTabClick("analytics")}
              className={`cursor-pointer rounded-xl  ${
                activeTab === "analytics"
                  ? "bg-blue-600 dark:bg-blue-500"
                  : "bg-blue-900"
              }`}
            >
              <CommonGrid
                title="Analytics"
                value={analyticsData ? analyticsData.total || "-" : "-"}
              />
            </div> */}
          </div>

          {/* Tab Details */}
          <div className="mt-0">
            <div className="p-4 mt-8 border rounded bg-white  dark:bg-gray-700">
              <h2 className="text-lg pt-4 pb-6 text-gray-900  dark:text-gray-100 font-bold">
                {activeTab.charAt(0).toUpperCase() + activeTab.slice(1)} Details
              </h2>
              {activeTab === "users" && (
                <div>
                  {/* User Tabs in Table Format */}
                  <table className="table-auto w-full border-collapse border border-gray-300">
                    <thead>
                      <tr className="bg-gray-200  dark:bg-gray-800  dark:text-gray-100">
                        <th className="border border-gray-300 px-4 py-2">
                          Category
                        </th>
                        <th className="border border-gray-300 px-4 py-2">
                          Count
                        </th>
                      </tr>
                    </thead>
                    <tbody className="text-gray-700 dark:text-gray-200">
                      <tr className="hover:bg-gray-100 dark:hover:bg-gray-600 ">
                        <td className="border border-gray-300 px-4 py-2">
                          Total Users
                        </td>
                        <td className="border border-gray-300 px-4 py-2 text-center">
                          {dashboardData.totalUsers}
                        </td>
                      </tr>
                      <tr className="hover:bg-gray-100 dark:hover:bg-gray-600 ">
                        <td className="border border-gray-300 px-4 py-2">
                          Active Users
                        </td>
                        <td className="border border-gray-300 px-4 py-2 text-center">
                          {dashboardData.activeUsers}
                        </td>
                      </tr>
                      <tr className="hover:bg-gray-100 dark:hover:bg-gray-600 ">
                        <td className="border border-gray-300 px-4 py-2">
                          Inactive Users
                        </td>
                        <td className="border border-gray-300 px-4 py-2 text-center">
                          {dashboardData.inactiveUsers}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}
              {activeTab === "groups" && (
                <div>
                  {/* Group Tab Content */}
                  <table className="table-auto w-full border-collapse border border-gray-300">
                    <thead>
                      <tr className="bg-gray-200 dark:bg-gray-800  dark:text-gray-100">
                        <th className="border border-gray-300 px-4 py-2">
                          User Name
                        </th>
                        <th className="border border-gray-300 px-4 py-2">
                          Status
                        </th>
                        <th className="border border-gray-300 px-4 py-2">
                          Total Groups
                        </th>
                      </tr>
                    </thead>
                    <tbody className="text-gray-700 dark:text-gray-200">
                      {dashboardData.userGroups.map(
                        (group: any, index: number) => (
                          <tr
                            key={index}
                            className="hover:bg-gray-100 dark:hover:bg-gray-600 "
                          >
                            <td className="border border-gray-300 px-4 py-2 ">
                              {group.user.username}
                            </td>
                            <td className="border border-gray-300 px-4 py-2 text-center text-green-500 dark:text-green-400 font-semibold">
                              {group.user.status}
                            </td>

                            <td className="border border-gray-300 px-4 py-2 text-center">
                              {group.totalGroups}
                            </td>
                          </tr>
                        )
                      )}
                    </tbody>
                  </table>
                </div>
              )}
              {activeTab === "contacts" && (
                <div>
                  {/* Contact Tab Content */}
                  <table className="table-auto w-full border-collapse border border-gray-300">
                    <thead>
                      <tr className="bg-gray-200 dark:bg-gray-800  dark:text-gray-100">
                        <th className="border border-gray-300 px-4 py-2">
                          User Name
                        </th>
                        <th className="border border-gray-300 px-4 py-2">
                          Status
                        </th>

                        <th className="border border-gray-300 px-4 py-2">
                          Total Contacts
                        </th>
                      </tr>
                    </thead>
                    <tbody className="text-gray-700 dark:text-gray-200">
                      {dashboardData.userContacts.map(
                        (contact: any, index: number) => (
                          <tr
                            key={index}
                            className="hover:bg-gray-100 dark:hover:bg-gray-600 "
                          >
                            <td className="border border-gray-300 px-4 py-2 ">
                              {contact.user.username}
                            </td>
                            <td className="border border-gray-300 px-4 py-2 text-center text-green-500 dark:text-green-400 font-semibold">
                              {contact.user.status}
                            </td>

                            <td className="border border-gray-300 px-4 py-2 text-center">
                              {contact.totalContacts}
                            </td>
                          </tr>
                        )
                      )}
                    </tbody>
                  </table>
                </div>
              )}
              {activeTab === "messages" && (
                <div className="flex flex-col space-y-6 mb-4">
                  {/* User Details Section */}

                  <table className="table-auto w-full border-collapse border border-gray-300">
                    <thead>
                      <tr className="bg-gray-200 dark:bg-gray-800  dark:text-gray-100">
                        <th className="border border-gray-300 px-4 py-2">
                          User Name
                        </th>
                        <th className="border border-gray-300 px-4 py-2">
                          Messages Sent
                        </th>
                        <th className="border border-gray-300 px-4 py-2">
                          Messages Delivered
                        </th>
                        <th className="border border-gray-300 px-4 py-2">
                          Messages Failed
                        </th>
                        <th className="border border-gray-300 px-4 py-2">
                          Messages Received
                        </th>
                        <th className="border border-gray-300 px-4 py-2">
                          Messages Read
                        </th>
                      </tr>
                    </thead>
                    <tbody className="text-gray-700 dark:text-gray-200">
                      {dashboardData.msgReport.msgCount.map(
                        (msg: any, index: number) => (
                          <tr
                            key={index}
                            className="hover:bg-gray-100 dark:hover:bg-gray-600 "
                          >
                            <td className="border border-gray-300 px-4 py-2">
                              {msg.user.username}
                            </td>
                            <td className="border border-gray-300 px-4 py-2 text-center">
                              {msg.sent}
                            </td>
                            <td className="border border-gray-300 px-4 py-2 text-center">
                              {msg.delivered}
                            </td>
                            <td className="border border-gray-300 px-4 py-2 text-center">
                              {msg.failed}
                            </td>
                            <td className="border border-gray-300 px-4 py-2 text-center">
                              {msg.received}
                            </td>
                            <td className="border border-gray-300 px-4 py-2 text-center">
                              {msg.read}
                            </td>
                          </tr>
                        )
                      )}
                      {/* Total Row */}
                      <tr className="bg-gray-100 font-bold dark:bg-gray-800  dark:text-gray-100">
                        <td className="border border-gray-300 px-4 py-2">
                          Total
                        </td>
                        <td className="border border-gray-300 px-4 py-2 text-center">
                          {dashboardData.msgReport.msgCount.reduce(
                            (total: number, msg: any) => total + msg.sent,
                            0
                          )}
                        </td>
                        <td className="border border-gray-300 px-4 py-2 text-center">
                          {dashboardData.msgReport.msgCount.reduce(
                            (total: number, msg: any) => total + msg.delivered,
                            0
                          )}
                        </td>
                        <td className="border border-gray-300 px-4 py-2 text-center">
                          {dashboardData.msgReport.msgCount.reduce(
                            (total: number, msg: any) => total + msg.failed,
                            0
                          )}
                        </td>
                        <td className="border border-gray-300 px-4 py-2 text-center">
                          {dashboardData.msgReport.msgCount.reduce(
                            (total: number, msg: any) => total + msg.received,
                            0
                          )}
                        </td>
                        <td className="border border-gray-300 px-4 py-2 text-center">
                          {dashboardData.msgReport.msgCount.reduce(
                            (total: number, msg: any) => total + msg.read,
                            0
                          )}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}
   
            </div>
          </div>
           {/* Always show analytics below */}

          <div className="p-4 mt-8 border rounded bg-white dark:bg-gray-700">
            <h2 className="text-lg pt-4 pb-6 text-gray-900 dark:text-gray-100 font-bold">Analytics</h2>
            {/* Analytics Query Controls - all in one row */}
            <form className="flex flex-wrap gap-4 mb-4 items-end" onSubmit={handleAnalyticsFilter}>
              <div>
                <label className="block text-xs font-semibold mb-1">Interval</label>
                <select
                  className="p-2 rounded border dark:bg-gray-800 dark:text-gray-100"
                  value={reportType}
                  onChange={e => setReportType(e.target.value as ReportInterval)}
                >
                  {Object.values(ReportInterval).map((interval) => (
                    <option key={interval} value={interval}>{interval}</option>
                  ))}
                </select>
              </div>
              {reportType === ReportInterval.CUSTOM && (
                <>
                  <div>
                    <label className="block text-xs font-semibold mb-1">Start Date</label>
                    <input
                      type="date"
                      className="p-2 rounded border dark:bg-gray-800 dark:text-gray-100"
                      value={startTime}
                      onChange={e => setStartTime(e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold mb-1">End Date</label>
                    <input
                      type="date"
                      className="p-2 rounded border dark:bg-gray-800 dark:text-gray-100"
                      value={endTime}
                      onChange={e => setEndTime(e.target.value)}
                    />
                  </div>
                </>
              )}
              <div>
  <label className="block text-xs font-semibold mb-1">User</label>
  <select
    className="p-2 rounded border dark:bg-gray-800 dark:text-gray-100"
    value={selectedUser || "All"}
    onChange={e => {
      const user = e.target.value;
      setSelectedUser(user);
    }}
  >
    <option value="All">All</option>
    {Array.from(
      new Set(
        analyticsData
          .flatMap((item: any) =>
            item.msgReport?.msgCount?.map((userItem: any) => userItem.user.username) || []
          )
      )
    ).map((username) => (
      <option key={username} value={username}>
        {username}
      </option>
    ))}
  </select>
</div>
              {/* <div>
                <label className="block text-xs font-semibold mb-1">User</label>
                <select
                  className="p-2 rounded border dark:bg-gray-800 dark:text-gray-100"
                  value={selectedUser || ""}
                  onChange={e => {
                    const user = e.target.value;
                    setSelectedUser(user);
                  }}
                >
                  {Array.from(
                    new Set(
                      analyticsData
                        .flatMap((item: any) =>
                          item.msgReport?.msgCount?.map((userItem: any) => userItem.user.username) || []
                        )
                    )
                  ).map((username) => (
                    <option key={username} value={username}>
                      {username}
                    </option>
                  ))}
                </select>
              </div> */}
              <div>
                <label className="block text-xs font-semibold mb-1">Chart type</label>
                <select
                  className="p-2 rounded border dark:bg-gray-800 dark:text-gray-100"
                  value={chartType}
                  onChange={e => setChartType(e.target.value as "bar" | "line" | "pie")}
                >
                  <option value="bar">Bar Chart</option>
                  <option value="line">Line Chart</option>
                  <option value="pie">Pie Chart</option>
                </select>
              </div>
              <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded">Filter</button>
            </form>
            {analyticsData && analyticsData.length > 0 ? (
              (() => {
                // let filteredAnalytics = analyticsData;
                // if (selectedUser) {
                //   // For each date, extract only the selected user's stats
                //   filteredAnalytics = analyticsData.map((item: any) => {
                //     const userStats = item.msgReport?.msgCount?.find((userItem: any) => userItem.user.username === selectedUser);
                //     return {
                //       ...item,
                //       msgReport: {
                //         ...item.msgReport,
                //         sent: userStats ? userStats.sent : 0,
                //         delivered: userStats ? userStats.delivered : 0,
                //         failed: userStats ? userStats.failed : 0,
                //         received: userStats ? userStats.received : 0,
                //         read: userStats ? userStats.read : 0,
                //         // msgCount is not needed for charting
                //       }
                //     };
                //   });
                // }


                  let filteredAnalytics = analyticsData;
  if (selectedUser && selectedUser !== "All") {
    // For each date, extract only the selected user's stats

    filteredAnalytics = analyticsData.map((item: any) => {
      const userStats = item.msgReport?.msgCount?.find(
        (userItem: any) => userItem.user.username === selectedUser
      );
      return {
        ...item,
        msgReport: {
          ...item.msgReport,
          sent: userStats ? userStats.sent : 0,
          delivered: userStats ? userStats.delivered : 0,
          failed: userStats ? userStats.failed : 0,
          received: userStats ? userStats.received : 0,
          read: userStats ? userStats.read : 0,
        },
      };
    });
  }
  console.log('filteredAnalytics:', filteredAnalytics,analyticsData);

                // Render analytics charts with filteredAnalytics
                return (
                  <div className="flex flex-col md:flex-row gap-8">
                    {/* Left: Date-based chart */}
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="font-semibold">Message Report (Daily)</h3>
                      </div>
                      <ResponsiveContainer width="100%" height={350}>
                        {(() => {
                          const weeklyChartData = getWeeklyChartData(filteredAnalytics, selectedUser);
                          const allUsers = Array.from(
                            new Set(
                              analyticsData
                                .flatMap((item: any) =>
                                  item.msgReport?.msgCount?.map((userItem: any) => userItem.user.username) || []
                                )
                            )
                          );
console.log('allUsers:', allUsers,weeklyChartData);
                          const COLORS = [
                            "#2563eb", "#22c55e", "#f87171", "#fbbf24", "#a78bfa", "#60a5fa", "#8884d8", "#82ca9d"
                          ];

                          if (chartType === "bar" || chartType === "line") {
                            return (
                              <ResponsiveContainer width="100%" height={350}>
                                {selectedUser === "All" ? (
                                  chartType === "bar" ? (
                                    <BarChart data={weeklyChartData}>
                                      <XAxis dataKey="date" />
                                      <YAxis allowDecimals={false} />
                                      <Tooltip content={<CustomTooltip />} />
                                      <Legend />
                                      {allUsers.map((username, idx) => (
                                        <Bar
                                          key={username}
                                          dataKey={username}
                                          name={`Sent (${username})`}
                                          fill={COLORS[idx % COLORS.length]}
                                          stackId="a"
                                          isAnimationActive={false}
                                        />
                                      ))}
                                    </BarChart>
                                  ) : (
                                    <LineChart data={weeklyChartData}>
                                      <XAxis dataKey="date" />
                                      <YAxis allowDecimals={false} />
                                      <Tooltip content={<CustomTooltip />} />
                                      <Legend />
                                      {allUsers.map((username, idx) => (
                                        <Line
                                          key={username}
                                          type="monotone"
                                          dataKey={username}
                                          name={`Sent (${username})`}
                                          stroke={COLORS[idx % COLORS.length]}
                                          isAnimationActive={false}
                                        />
                                      ))}
                                    </LineChart>
                                  )
                                ) : (
                                  chartType === "bar" ? (
                                    <BarChart data={weeklyChartData}>
                                      <XAxis dataKey="date" />
                                      <YAxis allowDecimals={false} />
                                      <Tooltip content={<CustomTooltip />} />
                                      <Legend />
                                      <Bar dataKey="Sent" fill={COLORS[0]} />
                                      <Bar dataKey="Delivered" fill={COLORS[1]} />
                                      <Bar dataKey="Failed" fill={COLORS[2]} />
                                      <Bar dataKey="Received" fill={COLORS[3]} />
                                      <Bar dataKey="Read" fill={COLORS[4]} />
                                    </BarChart>
                                  ) : (
                                    <LineChart data={weeklyChartData}>
                                      <XAxis dataKey="date" />
                                      <YAxis allowDecimals={false} />
                                      <Tooltip content={<CustomTooltip />} />
                                      <Legend />
                                      <Line type="monotone" dataKey="Sent" stroke={COLORS[0]} />
                                      <Line type="monotone" dataKey="Delivered" stroke={COLORS[1]} />
                                      <Line type="monotone" dataKey="Failed" stroke={COLORS[2]} />
                                      <Line type="monotone" dataKey="Received" stroke={COLORS[3]} />
                                      <Line type="monotone" dataKey="Read" stroke={COLORS[4]} />
                                    </LineChart>
                                  )
                                )}
                              </ResponsiveContainer>
                            );
                          } else {
                            // Pie chart for array: show total sent per date
                            return (
                              <PieChart>
                                <Pie
                                  data={weeklyChartData}
                                  dataKey="Sent"
                                  nameKey="date"
                                  cx="50%"
                                  cy="50%"
                                  outerRadius={120}
                                  label
                                >
                                  {weeklyChartData.map((entry, idx) => (
                                    <Cell key={`cell-${idx}`} fill={COLORS[idx % COLORS.length]} />
                                  ))}
                                </Pie>
                                <Tooltip content={<CustomTooltip />} />
                                <Legend />
                              </PieChart>
                            );
                          }
                        })()}
                      </ResponsiveContainer>
                    </div>
                    {/* Right: Total summary chart */}
                    <div className="flex-1">
                      <h3 className="font-semibold mb-4">Total Message Summary</h3>
                      <ResponsiveContainer width="100%" height={350} className={"pt-0"}>
                        {(() => {
                          // Use filteredAnalytics for summary
                          const summary = filteredAnalytics.reduce(
                            (acc: any, item: any) => {
                              acc.sent += item.msgReport?.sent || 0;
                              acc.delivered += item.msgReport?.delivered || 0;
                              acc.failed += item.msgReport?.failed || 0;
                              acc.received += item.msgReport?.received || 0;
                              acc.read += item.msgReport?.read || 0;
                              return acc;
                            },
                            { sent: 0, delivered: 0, failed: 0, received: 0, read: 0 }
                          );
                          const chartData = [
                            { name: "Sent", value: summary.sent },
                            { name: "Delivered", value: summary.delivered },
                            { name: "Failed", value: summary.failed },
                            { name: "Received", value: summary.received },
                            { name: "Read", value: summary.read },
                          ];
                          const COLORS = [
                            "#8884d8",
                            "#82ca9d",
                            "#f87171",
                            "#60a5fa",
                            "#fbbf24",
                          ];
                          if (chartType === "bar") {
                            return (
                              <BarChart
                                data={chartData}
                                margin={{
                                  top: 20,
                                  right: 30,
                                  left: 0,
                                  bottom: 5,
                                }}
                              >
                                <XAxis dataKey="name" />
                                <YAxis allowDecimals={false} />
                                <Tooltip content={<CustomTooltip />} />
                                <Legend />
                                <Bar dataKey="value" fill="#8884d8" />
                              </BarChart>
                            );
                          } else if (chartType === "line") {
                            return (
                              <LineChart
                                data={chartData}
                                margin={{
                                  top: 20,
                                  right: 30,
                                  left: 0,
                                  bottom: 5,
                                }}
                              >
                                <XAxis dataKey="name" />
                                <YAxis allowDecimals={false} />
                                <Tooltip content={<CustomTooltip />} />
                                <Legend />
                                <Line
                                  type="monotone"
                                  dataKey="value"
                                  stroke="#8884d8"
                                />
                              </LineChart>
                            );
                          } else {
                            return (
                              <PieChart>
                                <Pie
                                  data={chartData}
                                  dataKey="value"
                                  nameKey="name"
                                  cx="50%"
                                  cy="50%"
                                  outerRadius={120}
                                  label
                                >
                                  {chartData.map((entry, idx) => (
                                    <Cell
                                      key={`cell-${idx}`}
                                      fill={COLORS[idx % COLORS.length]}
                                    />
                                  ))}
                                </Pie>
                                <Tooltip content={<CustomTooltip />} />
                                <Legend />
                              </PieChart>
                            );
                          }
                        })()}
                      </ResponsiveContainer>
                    </div>
                  </div>
                );
              })()
            ) : (
              <div>Loading analytics...</div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}

// Custom Tooltip for recharts (single definition at the end)

const CustomTooltip = (props: TooltipProps<any, any>) => {
  const { active, payload, label } = props as any;
  if (!active || !payload || payload.length === 0) return null;

  // For "All" users, show each user's name and all metrics
  const isAllUser =
    payload.length > 0 &&
    payload[0].payload &&
    Object.keys(payload[0].payload).some(
      (k) => k.startsWith("__") && k.endsWith("_all")
    );

  if (isAllUser) {
    // Only show users that have a line/bar (i.e. Sent)
    const usernames = payload.map((p: any) => p.dataKey);

    return (
      <div className="bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded shadow-md p-2 text-xs">
        <div className="font-semibold mb-2 text-gray-900 dark:text-gray-100">{label}</div>
        {usernames.map((username: string) => {
          const userStats = payload[0].payload[`__${username}_all`] || {};
          return (
            <div key={username} className="mb-2">
              <div className="font-bold text-blue-600 dark:text-blue-300">{username}</div>
              <div className="flex flex-wrap gap-2 ml-2 mt-1">
                <span>Sent: <b>{userStats.sent ?? 0}</b></span>
                <span>Delivered: <b>{userStats.delivered ?? 0}</b></span>
                <span>Failed: <b>{userStats.failed ?? 0}</b></span>
                <span>Received: <b>{userStats.received ?? 0}</b></span>
                <span>Read: <b>{userStats.read ?? 0}</b></span>
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  // Fallback for single user or summary
  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded shadow-md p-2 text-xs">
      <div className="font-semibold mb-1 text-gray-900 dark:text-gray-100">{label}</div>
      {payload.map((entry: any, idx: number) => (
        <div key={idx} className="flex items-center gap-2 mb-1 px-4 py-1">
          <span className="inline-block w-2 h-2 rounded-full" style={{ background: entry.color }}></span>
          <span className="capitalize text-gray-700 dark:text-gray-200">{entry.name}:</span>
          <span className="font-bold text-gray-900 dark:text-gray-100">{entry.value}</span>
          {/* Display user name if available */}
          {/* {entry.payload && entry.payload.username && (
            <span className="ml-2 text-xs text-blue-600 dark:text-blue-300">
              ({entry.payload.username})
            </span>
          )} */}
        </div>
      ))}
    </div>
  );
};

export default Dashboard;
